import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import FormField from "../../../../components/common/FormField";
import {
  leadAncillariesFields,
  leadAncillariesHeading,
  leadChargesFields,
  leadChargesHeading,
  leadCostFields,
  leadCostHeading,
  leadEstimateFinalFields,
  leadEstimateInsuranceFields,
  leadEstimateInsuranceGeneralFields,
  leadEstimateNoteFields,
  leadGenInfoFields,
  leadGenInfoHeading,
  leadGenInoList,
  leadMaterialsFields,
  leadMaterialsHeading,
} from "../leadAllFields";
import CustomButton from "../../../../components/common/button/CustomButton";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  addLeadEstimateAction,
  getLeadEstimateAction,
  setLeadEstimateAction,
} from "../../../../store/reducers/leadSlice";
import { leadEstimateInitial } from "../../../../store/reducers/leadUtils";
import { getAllVehicles } from "../../../../store/reducers/vehicleSlice";
import {
  estimateChangeAction,
  estimateValidateForm,
  getTotal,
} from "./leadEstimateUtil";
import RenderEstimateTable from "./RenderEstimateTable";
import useStyles from "./leadEstimateStyle";
import EstimateActions from "./EstimateActions";

import { setNotificationAction } from "../../../../store/reducers/mmymSlice";
import {
  leadEstimateMaterialsFields,
  leadEstimateMaterialsHeading,
  leadEstimateNewFinalFields,
} from "../estimateAllFields";
import Notification from "../../../../components/common/notification/Notification";
import LeadDetailSection from "./LeadDetailSection";
import closeIcon from "../../../../assets/closeModel.png";
import checkedIcon from "../../../../assets/check-mark.png";
import { getLeadColor } from "../../customerInvoice/InvoiceUntil";
import { setLeadSendEmailTemplateResponseAction } from "../../../../store/reducers/emailTemplateSlice";
import { getDefaultServiceListAction } from "../../../../store/reducers/reviewSlice";
import {
  getBaseServiceType as getBaseServiceTypeHelper,
  getUniqueServiceType as getUniqueServiceTypeHelper,
} from "../serviceTypeUtils";

const LeadEstimateForm: FC<any> = ({ show, setShow, screenType }) => {
  const normalizeServiceName = (service: any) =>
    String(
      service?.name ||
        service?.typeName ||
        service?.service_name ||
        service?.type ||
        "",
    )
      .toLowerCase()
      .replace(/[,_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const normalizeServiceType = (service: any) =>
    String(service?.type || service?.service_name || service?.typeName || "")
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[,_-]+/g, "");

  const getBaseServiceTypeLocal = (serviceType: string) =>
    getBaseServiceTypeHelper(serviceType);

  const getOrderedServiceOptions = (services: any[] = []) => {
    const mappedServices = services
      .map((service: any) => ({
        id:
          service?.name ||
          service?.typeName ||
          service?.service_name ||
          service?.type ||
          "",
        name:
          service?.name ||
          service?.typeName ||
          service?.service_name ||
          service?.type ||
          "",
        description: service?.description || "",
        type:
          service?.type ||
          service?.service_name ||
          service?.typeName ||
          service?.name ||
          "",
        normalizedName: normalizeServiceName(service),
        typeKey: normalizeServiceType(service),
        orderIndex:
          service?.order_index !== undefined && service?.order_index !== null
            ? Number(service.order_index)
            : null,
      }))
      .filter((service: any) => !!service?.name);

    mappedServices.sort(
      (a: any, b: any) => (a.orderIndex || 999) - (b.orderIndex || 999),
    );

    const removalService = mappedServices.find((service: any) => {
      const key = service?.typeKey || normalizeServiceType(service);
      return key === "removal" || key === "removaltype";
    });

    const fullPackService = mappedServices.find(
      (service: any) =>
        (service?.typeKey || normalizeServiceType(service)) === "fullpack",
    );

    const remainingServices = mappedServices.filter(
      (service: any) =>
        service !== removalService &&
        service !== fullPackService &&
        (service?.typeKey || normalizeServiceType(service)) !== "removal" &&
        (service?.typeKey || normalizeServiceType(service)) !== "removaltype" &&
        (service?.typeKey || normalizeServiceType(service)) !== "fullpack",
    );

    const orderedServiceOptions = [
      ...(removalService ? [removalService] : []),
      ...(fullPackService ? [fullPackService] : []),
      ...remainingServices,
    ];

    return orderedServiceOptions;
  };

  const [load, setLoad] = useState(false);
  const classes = useStyles({ show, load });
  const [estimateForm, setEstimateForm]: any = useState(null);
  const [error, setError] = useState(null);
  const [vehiclesOptions, setVehiclesOptions] = useState([]);
  const [disabledField, setDisabledField] = useState(false);
  const [chargesError, setChargesError] = useState(false);
  const [costChargeError, setCostChargeError] = useState(false);
  const [isFieldValueChanged, setIsFieldValueChanged] = useState(false);
  const [isTesterSection, setIsTesterSection] = useState(true);
  const { leadDetails, selectedLeadEstimate } = useSelector(
    (state: any) => state.lead,
  );
  const { defaultServiceList = [] } = useSelector((state: any) => state.review);
  const { isSendEmailResponse } = useSelector(
    (state: any) => state.emailTemplates,
  );
  const { genenralInfoData } = useSelector((state: any) => state.document);
  const { vehicles } = useSelector((state: any) => state.vehicle);
  const { userProfile, settingWorkerModulePermission } = useSelector(
    (state: any) => state?.user,
  );

  useEffect(() => {
    if (show) {
      dispatch(getDefaultServiceListAction());
    }
  }, [show]);

  const [isCostSection, setIsCostSection] = useState(
    userProfile?.features?.includes("Cost") || false, // Safe fallback
  );

  const [isAnciliarySection, setIsAnciliarySection] = useState(
    userProfile?.features?.includes("Ancillary") || false,
  );
  const [isFullMaterialList, setIsFullMaterial] = useState(
    userProfile?.features?.includes("Full Material List") || false,
  );

  const serviceNameOptions = useMemo(
    () => getOrderedServiceOptions(defaultServiceList),
    [defaultServiceList],
    
  );

  // Match a saved service value (typeName/type) against the current defaultServiceList options.
// Handles case-mismatch (e.g. "into store" vs "Into Store") and returns the canonical option.
const getServiceOptionBySavedValue = (value: any) => {
  const savedName = normalizeServiceName({ name: value });
  const baseType = getBaseServiceTypeLocal(String(value || ""));

  return serviceNameOptions.find((option: any) => {
    const optionName = normalizeServiceName(option);
    const optionType = getBaseServiceTypeLocal(option?.type || "");
    return optionName === savedName || (baseType && optionType === baseType);
  });
};

// Merge options with any saved service that no longer exists in defaultServiceList
// (renamed/deleted service) so the dropdown doesn't render blank.
const getMergedServiceOptions = (services: any[] = []) => {
  const existingKeys = new Set(
    serviceNameOptions.map((opt: any) => normalizeServiceName(opt)),
  );
  const orphanOptions: any[] = [];
  const seenOrphans = new Set<string>();

  (services || []).forEach((service: any) => {
    const savedName = service?.typeName || service?.type || service?.service_name;
    if (!savedName) return;

    const matched = getServiceOptionBySavedValue(savedName);
    if (matched) return;

    const key = normalizeServiceName({ name: savedName });
    if (existingKeys.has(key) || seenOrphans.has(key)) return;
    seenOrphans.add(key);

    orphanOptions.push({
      id: savedName,
      name: savedName,
      description: service?.description || "",
      type: service?.type || savedName,
    });
  });

  return [...serviceNameOptions, ...orphanOptions];
};

  const getUniqueServiceType = (
    baseType: string,
    rowIndex: number,
    services: any[] = [],
  ) => getUniqueServiceTypeHelper(baseType, rowIndex, services);

  useEffect(() => {
    setIsTesterSection(
      userProfile?.features?.includes("Basic Work Diary") || false,
    );
    setIsCostSection(userProfile?.features?.includes("Cost") || false); // Sync profile changes
    setIsAnciliarySection(
      userProfile?.features?.includes("Ancillary") || false,
    );
    setIsFullMaterial(
      userProfile?.features?.includes("Full Material List") || false,
    );
  }, [userProfile]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (screenType !== "quotation") {
      if (
        leadDetails?.lead?.status &&
        ((leadDetails?.lead?.status !== "NEW" &&
          leadDetails?.lead?.status !== "NEW LEAD" &&
          leadDetails?.lead?.status !== "ESTIMATES") ||
          leadDetails?.lead?.status === "COMPLETED" ||
          leadDetails?.lead?.status === "DEBTOR" || leadDetails?.lead?.status === "STORAGE" ||
          leadDetails?.lead?.status === "EXTERNAL")
      ) {
        setDisabledField(true);
      } else {
        setDisabledField(false);
      }
    } else {
      if (
        leadDetails?.lead?.status &&
        (leadDetails?.lead?.status === "COMPLETED" ||
          leadDetails?.lead?.status === "JOB" || leadDetails?.lead?.status === "STORAGE")
      ) {
        setDisabledField(true);
      } else {
        setDisabledField(false);
      }
    }
  }, [leadDetails]);

  useEffect(() => {
    if (!show) return;

    // create services from default list
    let mappedDefaultServices =
      defaultServiceList?.map((service: any) => ({
        typeName: service?.name,
        description: service?.description || "",
        type: service?.type || service?.service_name || "",
        price: null,
        isAccepted: false,
        driverQty: null,
        porterQty: null,
        packerQty: null,
        vehicleQty: null,
        vehicleTypeId: "",
        wageCharge: null,
        fuelCharge: null,
        orderIndex:
          service?.order_index !== undefined && service?.order_index !== null
            ? Number(service.order_index)
            : null,
      })) || [];

    // sort according to backend order_index
    mappedDefaultServices.sort(
      (a: any, b: any) => (a.orderIndex || 999) - (b.orderIndex || 999),
    );

    // always keep Removal at top
    const removalService = mappedDefaultServices.find(
      (s: any) => s.type?.toLowerCase() === "removal",
    );

    // always keep Full Pack at second
    const fullPackService = mappedDefaultServices.find(
      (s: any) => s.type?.toLowerCase() === "fullpack",
    );

    // remaining services
    const remainingServices = mappedDefaultServices.filter(
      (s: any) =>
        s.type?.toLowerCase() !== "removal" &&
        s.type?.toLowerCase() !== "fullpack",
    );

    // final ordered list
    mappedDefaultServices = [
      ...(removalService ? [removalService] : []),
      ...(fullPackService ? [fullPackService] : []),
      ...remainingServices,
    ];

    // final sequential order indexes
    mappedDefaultServices = mappedDefaultServices.map(
      (service: any, index: number) => ({
        ...service,
        orderIndex: index + 1,
      }),
    );
    const createEmptyServiceRow = (orderIndex: number) => ({
      typeName: "",
      description: "",
      type: "",
      price: null,
      isAccepted: false,
      driverQty: null,
      porterQty: null,
      packerQty: null,
      vehicleQty: null,
      vehicleTypeId: "",
      wageCharge: null,
      fuelCharge: null,
      orderIndex,
    });

    console.log("selectedLeadEstimate", selectedLeadEstimate);
    const normalizeExistingServices = (services: any[] = []) =>
      services.map((service: any, idx: number) => {
        const savedServiceName =
          service?.typeName ||
          service?.service_name ||
          service?.name ||
          service?.type ||
          "";

          // ✅ Match saved value against current defaultServiceList to get canonical
    // name + type + description. Fixes casing mismatch and empty description.
    const matchedOption = getServiceOptionBySavedValue(
      savedServiceName || service?.type,
    );

        return {
          ...service,
         typeName: matchedOption?.name || savedServiceName,
      type: service?.type || matchedOption?.type || "",
      description:
        service?.description || matchedOption?.description || "",
          orderIndex: service?.orderIndex || idx + 1,
        };
      });

    // CASE 1 -> Estimate already saved
    if (selectedLeadEstimate?.estimateId) {
      setEstimateForm({
        ...selectedLeadEstimate,
        services: normalizeExistingServices(
          selectedLeadEstimate?.services || [],
        ),
      });
    }

    // CASE 2 -> New estimate
    else {
      const existingServices = selectedLeadEstimate?.services;
      setEstimateForm({
        ...leadEstimateInitial,
        ...selectedLeadEstimate,
        services:
          existingServices?.length > 0
            ? normalizeExistingServices(existingServices)
            : [createEmptyServiceRow(1)],
      });
    }
  }, [selectedLeadEstimate, defaultServiceList, show]);

  useEffect(() => {
    const insurancePercentage = estimateForm?.insurancePercentage;
    const price = estimateForm?.services?.[0]?.price;
    const userInsurance = Number(userProfile?.extendedLiabilityPercent || 0);
    // If insurancePercentage is not yet set and screen is quotation, then fill it
    if (
      screenType === "quotation" &&
      estimateForm?.services?.length > 0 &&
      (insurancePercentage === null || insurancePercentage === undefined) &&
      userInsurance > 0
    ) {
      const insuranceAmount = price
        ? (userInsurance / 100) * Number(price)
        : null;
      setEstimateForm((prev: any) => ({
        ...prev,
        insurancePercentage: userInsurance,
        insuranceAmount,
      }));
    }
  }, [
    estimateForm?.insurancePercentage,
    estimateForm?.services?.[0]?.price,
    userProfile?.extendedLiabilityPercent,
    screenType,
  ]);

  useEffect(() => {
    setTimeout(() => {
      setLoad(true);
    }, 0);
    return () => {
      setLoad(false);
    };
  }, []);

  useEffect(() => {
    if (genenralInfoData?.id) {
      setEstimateForm((prevState: any) => ({
        ...prevState,
        generalInfo: {
          ...prevState.generalInfo, // Preserve existing values
          driverWage: genenralInfoData?.driver_wage
            ? Number(genenralInfoData.driver_wage)
            : null,
          porterWage: genenralInfoData?.porter_wage
            ? Number(genenralInfoData.porter_wage)
            : null,
          packerWage: genenralInfoData?.packer_wage
            ? Number(genenralInfoData.packer_wage)
            : null,
          contentsValue: genenralInfoData?.contents_value
            ? Number(genenralInfoData.contents_value)
            : null,
        },
      }));
    }
  }, []);

  useEffect(() => {
    if (leadDetails?.lead?.status !== "NEW") {
      dispatch(
        getLeadEstimateAction({
          generatedId: leadDetails?.lead?.generated_id,
          screenType,
        }),
      );
      dispatch(setLeadSendEmailTemplateResponseAction(false));
    }
  }, [leadDetails?.lead?.generated_id, isSendEmailResponse]);

  useEffect(() => {
    if (vehicles && vehicles.length) {
      const vehiclesLocal: any = [];
      vehicles &&
        vehicles.map((ele: any) => {
          vehiclesLocal.push({ id: ele?.type_id, name: ele?.type_name });
        });
      setVehiclesOptions(vehiclesLocal);
    } else {
      setVehiclesOptions([]);
    }
  }, [vehicles]);

  const onChangeAction = (
    event: any,
    section?: any,
    index?: any,
    valueFormat?: any,
  ) => {
    const { estimateData } = estimateChangeAction(
      event,
      section,
      index,
      valueFormat,
      estimateForm,
      vehicles,
    );

    if (
      section === "services" &&
      event?.target?.id === "typeName" &&
      estimateData?.services?.[index]
    ) {
      const selectedServiceId = String(event?.target?.value || "");
      const selectedService = serviceNameOptions?.find((service: any) => {
        const byName =
          normalizeServiceName(service) ===
          normalizeServiceName({ name: selectedServiceId });
        const byType =
          getBaseServiceTypeLocal(service?.type || "") ===
          getBaseServiceTypeLocal(selectedServiceId);

        return byName || byType;
      });

      const nextServices = [...estimateData.services];
      const currentServiceRow = nextServices[index];

      const resolvedService = selectedService;
      const fallbackType =
        selectedService?.type ||
        currentServiceRow?.type ||
        selectedServiceId ||
        normalizeServiceType({ typeName: selectedServiceId });
      const selectedBaseType = getBaseServiceTypeLocal(fallbackType);
      const selectedType =
        index === 0 ? "removal" : selectedBaseType || fallbackType;
      const uniqueSelectedType =
        index === 0
          ? "removal"
          : getUniqueServiceType(selectedType, index, nextServices);

      nextServices[index] = {
        ...currentServiceRow,
        typeName:
          resolvedService?.name || resolvedService?.id || selectedServiceId,
        type: uniqueSelectedType,
        description:
          resolvedService?.description || currentServiceRow?.description || "",
      };

      let updatedServices = nextServices;

      if (index === 0 && nextServices.length === 1 && selectedServiceId) {
        updatedServices = [
          ...nextServices,
          {
            typeName: "",
            description: "",
            type: "",
            price: null,
            driverQty: null,
            porterQty: null,
            packerQty: null,
            vehicleQty: null,
            vehicleTypeId: "",
            wageCharge: null,
            fuelCharge: null,
            orderIndex: 2,
          },
        ];
      }

      setEstimateForm({
        ...estimateData,
        services: updatedServices,
      });
    } else {
      setEstimateForm(estimateData);
    }

    setIsFieldValueChanged(true); // Set field value changed flag
  };

  const onBlurAction = (event: any) => {
    const { isError, localError } = estimateValidateForm(estimateForm);
    setError(localError);
  };

  const submitHandler = () => {
    const { isError, localError } = estimateValidateForm(
      estimateForm,
      isCostSection,
      screenType,
    );
    setError(localError);
    // Updated error handling
    setChargesError(
      localError?.quoteTotal ||
        estimateForm?.quoteTotal === null ||
        estimateForm?.quoteTotal === "" ||
        estimateForm?.quoteTotal === 0,
    );
    if (isCostSection) {
      setCostChargeError(
        localError?.costTotal ||
          estimateForm?.costTotal === null ||
          estimateForm?.costTotal === "" ||
          estimateForm?.costTotal === 0,
      );
    }

    if (!isError) {
      // ✅ Clone form
      const modifiedEstimateForm = { ...estimateForm };

      // ✅ Remove generalInfo
      delete modifiedEstimateForm.generalInfo;

      // ✅ Remove vehicles from each service
      if (modifiedEstimateForm.services?.length > 0) {
        modifiedEstimateForm.services = modifiedEstimateForm.services.map(
          (service: any) => {
            const { vehicles, ...rest } = service; // 🔥 remove vehicles
            return rest;
          },
        );
      }

      console.log("final payload:", modifiedEstimateForm);

      dispatch(
        addLeadEstimateAction({
          body: { ...modifiedEstimateForm },
          generatedId: leadDetails?.lead?.generated_id,
          screenType: screenType,
        }),
      );
      setIsFieldValueChanged(false); // Reset field value changed flag
    }
    setIsShowActionCalled(false); // Reset action type
  };

  const handleAddExtraService = () => {
    setEstimateForm((prevState: any) => {
      const currentMaxOrderIndex = Math.max(
        0,
        ...(prevState?.services || [])
          .map((service: any) => Number(service?.orderIndex))
          .filter((order: number) => Number.isFinite(order)),
      );
      return {
        ...prevState,
        services: [
          ...prevState.services,
          {
            typeName: "",
            description: "",
            type: `extra_service_${prevState?.services?.length + 1}`,
            price: null,
            driverQty: null,
            porterQty: null,
            packerQty: null,
            vehicleQty: null,
            vehicleTypeId: "",
            wageCharge: null,
            fuelCharge: null,
            orderIndex: prevState?.services?.length + 1,
          },
        ],
      };
    });
  };

  // Function to delete a service by index
  const handleDeleteServiceRow = (index: number) => {
    setEstimateForm((prevState: any) => {
      const updatedServices = prevState.services.filter(
        (service: any, i: number) => i !== index,
      );

      // Call `estimateChangeAction` directly after deleting a service
      const { estimateData } = estimateChangeAction(
        { target: { name: "services", value: updatedServices } }, // Simulated event
        "services",
        index,
        undefined, // Pass valueFormat if needed
        { ...prevState, services: updatedServices },
        vehicles,
      );

      return estimateData; // Update state with modified estimate data
    });
  };

  const filteredLeadChargesHeading =
    isCostSection && screenType === "quotation"
      ? leadChargesHeading // Show all headings if isCostSection is true
      : leadChargesHeading.filter(
          (heading) =>
            ![
              "Drivers",
              "Porters",
              "Packers",
              "Wages",
              "Vehicles",
              "Fuel",
            ].includes(heading.label),
        ); // Remove cost-related headings if isCostSection is false

  const filteredLeadChargesFields =
    isCostSection && screenType === "quotation"
      ? leadChargesFields // Show all fields if isCostSection is true
      : leadChargesFields.filter(
          (field) =>
            ![
              "driverQty",
              "porterQty",
              "packerQty",
              "wageCharge",
              "vehicleTypeId",
              "vehicleQty",
              "fuelCharge",
            ].includes(field.id),
        ); // Remove cost-related fields if isCostSection is false

        const mergedServiceOptions = getMergedServiceOptions(estimateForm?.services);

  const serviceDropdownLeadChargesFields = filteredLeadChargesFields.map(
    (field: any) => {
      if (field?.id !== "typeName") return field;
      return {
        ...field,
        type: "select",
        placeHolder: "Select service",
        options: mergedServiceOptions,
      };
    },
  );

  const [isShowActionCalled, setIsShowActionCalled] = useState(false);

  useEffect(() => {
    if (isShowActionCalled) {
      submitHandler();
      setIsShowActionCalled(false);
    }
  }, [isShowActionCalled]);

  return (
    <div
      id="estimate-popup-fixed"
      className={classes.estimatePopupContainer}
      style={{
        zIndex: show ? 11 : -1,
      }}
    >
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sm={12}
          sx={{ height: "45px", zIndex: 99, paddingLeft: "0px" }}
        >
          <div className={classes.popupTitle}>
            <Box
              sx={{
                display: "inline-block",
                px: 3,
                py: 1,
                backgroundColor: getLeadColor(
                  screenType === "quotation" ? "QUOTE" : "ESTIMATES",
                ),
                color: "white",
                clipPath:
                  "polygon(75% 0%, 100% 50%, 75% 100%, 0% 100%, 25% 50%, 0% 0%)",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  marginLeft: "2px",
                  paddingLeft: "6px !important",
                  textAlign: "center",
                }}
                variant="subtitle1"
              >
                {screenType ? "Quote" : "Estimate"}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "20px",
                color: getLeadColor(
                  screenType === "quotation" ? "QUOTE" : "ESTIMATES",
                ),
              }}
              variant="subtitle1"
            >
              {screenType ? "Quotation" : "Estimate"}
            </Typography>
            <Box>
              {disabledField || leadDetails?.lead?.status === "JOB" ? (
                ""
              ) : (
                <img
                  onClick={() => submitHandler()}
                  src={checkedIcon}
                  style={{
                    cursor: "pointer",
                    height: "26px",
                    width: "26px",
                    marginRight: 10,
                  }}
                  alt="save"
                />
              )}
              <img
                src={closeIcon}
                style={{ cursor: "pointer", height: "26px", width: "26px" }}
                onClick={() => {
                  setShow(false);
                  setEstimateForm(null);
                  setError(null);
                  dispatch(
                    setNotificationAction({ message: [], severity: "error" }),
                  );
                }}
                alt="cross-icon"
              />
            </Box>
            {/* <CustomButton
              title="Return"
              onClick={() => {
                setShow(false);
                setEstimateForm(null);
                setError(null);
                dispatch(
                  setNotificationAction({ message: [], severity: "error" })
                );
              }}
            /> */}
          </div>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          sx={{
            opacity: load ? 1 : 0,
            paddingLeft: "0px !important",
          }}
        >
          <Notification />
          {disabledField && (
            <div
              style={{
                fontSize: "14px",
                color: "#FF0000",
                background: "rgb(239 209 209)",
                marginBottom: "10px",
                padding: "10px",
              }}
            >
              {screenType !== "quotation" ? "Estimate" : "Quote"} is read only
              due to moved on next step.
            </div>
          )}
          <Grid container spacing={2} sx={{ fontSize: "14px" }}>
            <Grid item xs={12} sm={12} sx={{ paddingLeft: "0px !important" }}>
              <LeadDetailSection leadDetails={leadDetails} />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={isCostSection && screenType === "quotation" ? 12 : 12}
          sm={isCostSection && screenType === "quotation" ? 12 : 12}
          sx={{
            opacity: load ? 1 : 0,
            paddingLeft: "0px !important",
            paddingTop: "0px !important",
            backgroundColor: "#E5F4F1",
            borderRadius: "5px",
          }}
        >
          <div className={classes.container} style={{ paddingBottom: 10 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} sx={{ paddingLeft: "0px !important" }}>
                <div
                  className={classes.container}
                  style={{
                    height: "calc(100% - 45px)",
                    paddingBottom: 0,
                    border:
                      isCostSection && screenType === "quotation"
                        ? costChargeError || chargesError
                          ? "1px solid red"
                          : "1px solid #DDD"
                        : chargesError
                          ? "1px solid red"
                          : "1px solid #DDD",
                  }}
                >
                  {/* <Typography variant="subtitle1">Services</Typography> */}
                  <RenderEstimateTable
                    {...{
                      estimateForm,
                      onChangeAction,
                      handleDeleteServiceRow,
                      vehiclesOptions,
                      onBlurAction,
                      error,
                      tableHeadding: filteredLeadChargesHeading,
                      tableContent: estimateForm?.services,
                      fields: serviceDropdownLeadChargesFields,
                      additionRow: (
                        <>
                          {estimateForm?.services?.length >= 1 &&
                            estimateForm?.services?.filter(
                              (service: any) =>
                                !service.isAccepted &&
                                ![
                                  "clearance",
                                  "intostore",
                                  "outofstore",
                                  "removal",
                                  "fullpack",
                                  "unpack",
                                  "dismantle",
                                  "fragilepack",
                                  "vehiclehire",
                                  "exportpack",
                                  "storage",
                                ].includes(normalizeServiceType(service)),
                            ).length < 3 && (
                              // {estimateForm?.services?.length < 6 && (
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    paddingTop: "10px!important",
                                  }}
                                ></TableCell>
                                <TableCell
                                  colSpan={
                                    isCostSection && screenType === "quotation"
                                      ? 9
                                      : 2
                                  }
                                ></TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    paddingTop: "10px!important",
                                  }}
                                >
                                  <CustomButton
                                    disabled={disabledField}
                                    style={{
                                      marginRight: "10px",
                                      fontSize: "10px",
                                      padding: "0px 6px",
                                      fontWeight: 600,
                                      backgroundColor: "#fff",
                                    }}
                                    variant="outlined"
                                    title="+ More"
                                    onClick={handleAddExtraService}
                                  />
                                </TableCell>
                              </TableRow>
                            )}

                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                paddingTop: "20px!important",
                              }}
                            >
                              Total
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                paddingTop: "20px!important",
                                paddingLeft: "10px !important",
                                textAlign: "left",
                              }}
                            >
                              £
                              {getTotal(
                                "serivesCost",
                                estimateForm,
                              ).toLocaleString("en-GB")}
                            </TableCell>
                            {isCostSection && screenType === "quotation" && (
                              <>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    paddingTop: "20px!important",
                                  }}
                                >
                                  {getTotal("wages", estimateForm)}
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    paddingTop: "20px!important",
                                  }}
                                >
                                  £
                                  {getTotal(
                                    "costsCost",
                                    estimateForm,
                                  ).toLocaleString("en-GB")}
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        </>
                      ),
                      section: "services",
                      disabled: disabledField,
                    }}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Grid>
        {/* Material */}
        <Grid
          item
          xs={isCostSection && screenType === "quotation" ? 12 : 12}
          sm={isCostSection && screenType === "quotation" ? 12 : 12}
          sx={{
            opacity: load ? 1 : 0,
            paddingLeft: "0px !important",
            paddingTop: "10px !important",
          }}
        >
          <div
            className={classes.container}
            style={{
              backgroundColor: estimateForm?.requirements?.some(
                (req: any) =>
                  req.service_name === "material" && req.is_accepted,
              )
                ? "#E3FCEF"
                : "none",
              background: "#E5F4F1",
            }}
          >
            {" "}
            <RenderEstimateTable
              {...{
                estimateForm,
                onChangeAction,
                vehiclesOptions,
                onBlurAction,
                error,
                screenType,
                tableHeadding:
                  screenType !== "quotation"
                    ? leadEstimateMaterialsHeading
                    : leadMaterialsHeading,
                tableContent: estimateForm?.materials,
                fields:
                  screenType !== "quotation"
                    ? leadEstimateMaterialsFields
                    : leadMaterialsFields,
                additionRow: (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell colSpan={2}>
                      <FormControlLabel
                        label="Material Price Chargeable"
                        labelPlacement="start"
                        control={
                          <Checkbox
                            sx={{
                              marginTop: "16px",
                              marginLeft: "6px !important",
                              width: "auto !important",
                            }}
                            checked={estimateForm?.materialPriceChargeable}
                            disabled={disabledField}
                            onChange={(e: any) => onChangeAction(e)}
                            name="materialPriceChargeable"
                            id="materialPriceChargeable"
                            color="primary"
                          />
                        }
                        sx={{
                          marginLeft: "0px !important",
                          "& .MuiFormControlLabel-label": {
                            fontSize: "13px", // Set your desired font size here
                            display: "flex",
                          },
                        }}
                      />
                    </TableCell>
                    {screenType === "quotation" && <TableCell></TableCell>}
                    <TableCell sx={{ fontWeight: 600 }}>
                      £{getTotal("total", estimateForm).toLocaleString("en-GB")}
                    </TableCell>
                    {screenType === "quotation" && (
                      <>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {getTotal("volume", estimateForm)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          £
                          {getTotal("cost", estimateForm).toLocaleString(
                            "en-IN",
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ),
                section: "materials",
                disabled: disabledField,
              }}
            />
          </div>
        </Grid>
        {/* Charges */}
        <Grid
          item
          xs={12}
          sm={12}
          sx={{
            opacity: load ? 1 : 0,
            // order: isCostSection ? 1 : 2,
            paddingLeft: "0px !important",
            background: "#E5F4F1",
            borderRadius: "5px",
            border: "1px solid #ddd",
            marginTop: "10px",
            paddingBottom: "10px",
          }}
        >
          {isCostSection && screenType === "quotation" && (
            <Grid container spacing={2} sx={{ marginBottom: "16px" }}>
              {isCostSection && screenType === "quotation" && (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ paddingLeft: "0px !important" }}
                >
                  <div className={classes.container}>
                    <RenderEstimateTable
                      {...{
                        estimateForm,
                        onChangeAction,
                        vehiclesOptions,
                        onBlurAction,
                        error,
                        tableHeadding: leadGenInfoHeading,
                        tableContent: leadGenInoList,
                        fields: leadGenInfoFields,
                        additionRow: null,
                        section: "generalInfo",
                        disabled: disabledField,
                      }}
                    />
                  </div>
                </Grid>
              )}
              {isAnciliarySection && screenType === "quotation" && (
                <Grid
                  item
                  xs={12}
                  sm={isCostSection && screenType === "quotation" ? 6 : 12}
                  sx={{ paddingLeft: "0px !important" }}
                >
                  <div className={classes.container}>
                    <RenderEstimateTable
                      {...{
                        estimateForm,
                        onChangeAction,
                        vehiclesOptions,
                        onBlurAction,
                        error,
                        tableHeadding: leadAncillariesHeading,
                        tableContent: estimateForm?.ancillaries,
                        fields: leadAncillariesFields,
                        additionRow: (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Total
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {getTotal("ancillaries", estimateForm)}
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        ),
                        section: "ancillaries",
                        disabled: disabledField,
                      }}
                    />
                  </div>
                </Grid>
              )}
            </Grid>
          )}

          {/* payment method */}
          {screenType === "quotation" && (
            <>
              <Grid
                container
                spacing={1}
                sx={{
                  marginLeft: "0px !important",
                  width: "100% !important",
                  paddingTop: "10px",
                }}
              >
                <Grid
                  item
                  xs={12}
                  sm={1.5}
                  className={classes.labelBox}
                  alignItems="center"
                  sx={{
                    margin: "0!important",
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "14px !important",
                  }}
                >
                  <InputLabel
                    sx={{
                      color: "#333333",
                      fontWeight: 600,
                      fontSize: "17px",
                      textWrap: "wrap",
                      lineHeight: 1.1,
                    }}
                    shrink
                    htmlFor={"paymentMethod"}
                  >
                    Payment method
                  </InputLabel>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  style={{
                    padding: "0px 0px 0px 8px",
                    display: "flex",
                    justifyContent: "center",
                    paddingLeft: "16px !important",
                  }}
                >
                  <FormField
                    // key={field.id}
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                      e.preventDefault()
                    }
                    {...{
                      id: "paymentMethod",
                      type: "select",
                      variant: "outlined", // Adding this variant prop
                      isNonMarginTop: true,
                      value: estimateForm?.paymentMethod,
                      options: ["Debit", "Credit", "Cash"],
                      disabled: disabledField,
                      InputProps: {
                        sx: {
                          padding: "2px 4px",
                          height: "36px",
                        },
                      },

                      onChange: (e: any) => onChangeAction(e, "generalInfo", 0),
                      onBlur: onBlurAction,
                    }}
                  />
                </Grid>
              </Grid>

              {/* Insursnce UI */}
              <Grid
                container
                spacing={1}
                sx={{
                  marginLeft: "0px !important",
                  width: "100% !important",
                }}
              >
                <Grid
                  item
                  xs={12}
                  sm={1.5}
                  // className={classes.labelBox}
                  alignItems="center"
                  sx={{
                    margin: "0!important",
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "14px !important",
                  }}
                >
                  <InputLabel
                    sx={{
                      color: estimateForm?.requirements?.some(
                        (req: any) =>
                          req.service_name === "insurance" && req.is_accepted,
                      )
                        ? "#008E72"
                        : "#333333",
                      fontWeight: 600,
                      fontSize: "18px",
                      textWrap: "wrap",
                      lineHeight: 1.1,
                    }}
                    shrink
                    htmlFor={"paymentMethod"}
                  >
                    Insurance :
                  </InputLabel>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={2}
                  sx={{
                    display: "flex",
                    paddingLeft: "0px !important",
                    alignItems: "center",
                    paddingTop: "0px !important",
                  }}
                >
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      display: "flex",
                      marginTop: "0px",
                      alignItems: "center",
                    }}
                  >
                    <>
                      {leadEstimateInsuranceGeneralFields.map(
                        (field: any, idx: number) => {
                          return (
                            <FormField
                              key={idx}
                              onPaste={(
                                e: React.ClipboardEvent<HTMLInputElement>,
                              ) => e.preventDefault()}
                              onKeyDown={(event: any) => {
                                if (
                                  event.keyCode === 8 ||
                                  event.keyCode === 9 ||
                                  event.keyCode === 37 ||
                                  event.keyCode === 38 ||
                                  event.keyCode === 39 ||
                                  event.keyCode === 40 ||
                                  event.keyCode === 18 ||
                                  event.keyCode === 17 ||
                                  event.ctrlKey
                                )
                                  return;

                                if (
                                  field?.patternAllowed &&
                                  !field?.patternAllowed.test(event.key)
                                ) {
                                  event.preventDefault();
                                } else if (
                                  field?.patternAllowed &&
                                  !field?.patternAllowed.test(
                                    event.target.value + event.key,
                                  )
                                ) {
                                  event.preventDefault();
                                }
                              }}
                              {...{
                                ...field,
                                variant: "outlined", // Adding this variant prop
                                isNonMarginTop: true,
                                value: estimateForm?.[field?.id],
                                disabled: disabledField,
                                inSingleGridForm: true,
                                lblBackgroundColor:
                                  estimateForm?.requirements?.some(
                                    (req: any) =>
                                      req.service_name === "insurance" &&
                                      req.is_accepted,
                                  )
                                    ? "#E3FCEF"
                                    : "none",
                                InputProps: {
                                  ...(field?.inputProps && field?.inputProps),
                                  autocomplete: "off",
                                  sx: {
                                    padding: "2px 4px",
                                    height: "36px",
                                  },
                                },
                                onChange: (e: any) =>
                                  onChangeAction(
                                    e,
                                    "generalInfo",
                                    0,
                                    field?.valueFormat,
                                  ),
                                onBlur: onBlurAction,
                              }}
                            />
                          );
                        },
                      )}
                    </>
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{ display: "flex", justifyContent: "start" }}
                >
                  {leadEstimateInsuranceFields.map(
                    (field: any, idx: number) => {
                      return (
                        <FormField
                          key={idx}
                          onPaste={(
                            e: React.ClipboardEvent<HTMLInputElement>,
                          ) => e.preventDefault()}
                          onKeyDown={(event: any) => {
                            if (
                              event.keyCode === 8 ||
                              event.keyCode === 9 ||
                              event.keyCode === 37 ||
                              event.keyCode === 38 ||
                              event.keyCode === 39 ||
                              event.keyCode === 40 ||
                              event.keyCode === 18 ||
                              event.keyCode === 17 ||
                              event.ctrlKey
                            )
                              return;

                            if (
                              field?.patternAllowed &&
                              !field?.patternAllowed.test(event.key)
                            ) {
                              event.preventDefault();
                            } else if (
                              field?.patternAllowed &&
                              !field?.patternAllowed.test(
                                event.target.value + event.key,
                              )
                            ) {
                              event.preventDefault();
                            }
                          }}
                          {...{
                            ...field,
                            type: field?.type,
                            variant: "outlined", // Adding this variant prop
                            isNonMarginTop: true,
                            value: estimateForm?.[field?.id],
                            disabled: disabledField,
                            inSingleGridForm: true,
                            GridFieldStyle: {
                              marginRight: "8px",
                            },
                            InputProps: {
                              ...field?.inputProps,
                              autocomplete: "off",
                              sx: {
                                padding: "2px 4px",
                                height: "36px",
                              },
                            },
                            onChange: (e: any) =>
                              onChangeAction(
                                e,
                                "generalInfo",
                                0,
                                field?.valueFormat,
                              ),
                            onBlur: onBlurAction,
                          }}
                        />
                      );
                    },
                  )}
                </Grid>
              </Grid>
            </>
          )}
          {/* final field */}
          <div
            style={{
              padding: "10px 10px 0 10px",
            }}
          >
            <Grid container spacing={1}>
              {(screenType !== "quotation"
                ? leadEstimateNewFinalFields
                : leadEstimateFinalFields
              ).map((field: any, idx: number) => {
                // special case for type === "title"
                if (field.type === "title") {
                  return (
                    <Grid
                      key={idx}
                      item
                      xs={12}
                      sm={field.md || 1.5}
                      className={classes.labelBox}
                      sx={{
                        margin: "10!important",
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: "10px !important",
                      }}
                    >
                      <InputLabel
                        sx={{
                          color: "#333333",
                          fontWeight: 600,
                          fontSize: "18px",
                          textWrap: "wrap",
                          lineHeight: 1.1,
                        }}
                        shrink
                      >
                        {field.label}
                      </InputLabel>
                    </Grid>
                  );
                }

                // default input field
                return (
                  <FormField
                    // key={field.id}
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                      e.preventDefault()
                    }
                    onKeyDown={(event: any) => {
                      if (
                        event.keyCode === 8 ||
                        event.keyCode === 9 ||
                        event.keyCode === 37 ||
                        event.keyCode === 38 ||
                        event.keyCode === 39 ||
                        event.keyCode === 40 ||
                        event.keyCode === 18 ||
                        event.keyCode === 17 ||
                        event.ctrlKey
                      )
                        return;

                      if (
                        field?.patternAllowed &&
                        !field?.patternAllowed.test(event.key)
                      ) {
                        event.preventDefault();
                      } else if (
                        field?.patternAllowed &&
                        !field?.patternAllowed.test(
                          event.target.value + event.key,
                        )
                      ) {
                        event.preventDefault();
                      }
                    }}
                    {...{
                      ...field,
                      id: field?.id,
                      label:
                        field?.label?.[
                          screenType === "quotation" ? "quotation" : "estimate"
                        ],
                      variant: "outlined", // Adding this variant prop
                      // value: field?.id ==='quoteExpiresOn' ? screenType === "quotation"
                      // ? dayjs().add(3, "months")
                      // : ""
                      value:
                        field?.id === "quoteTotal" || field?.id === "costTotal"
                          ? Number(
                              estimateForm?.[field.id] ?? 0,
                            ).toLocaleString("en-GB")
                          : estimateForm && estimateForm[field.id],
                      isNonMarginTop: true,
                      disabled:
                        field?.id === "quoteExpiresOn"
                          ? isTesterSection
                          : field?.disabled || disabledField,
                      GridFieldStyle: {
                        ...field?.GridFieldStyle,
                        paddingLeft:
                          field?.id === "quoteTotal" ||
                          field?.id === "contentValue"
                            ? "0px !important"
                            : field?.id === "costTotal"
                              ? "5px !important"
                              : "0px !important",
                      },
                      InputProps: {
                        ...field?.inputProps,
                        sx: {
                          // ...field?.inputProps?.sx,
                          padding: "2px 4px",
                          height: "36px",
                        },
                      },
                      onChange: onChangeAction,
                      onBlur: onBlurAction,
                      error: error && error?.[field?.id],
                    }}
                  />
                );
              })}
            </Grid>
          </div>
        </Grid>
        {/* notes section */}
        <Grid
          item
          xs={12}
          sm={12}
          sx={{
            // order: isCostSection ? 2 : 1, // Move to last if cost features are available
            marginTop:
              isCostSection && screenType === "quotation" ? "10px" : "10px", //
            padding: "6px 10px 10px 10px !important",
            background: "#E5F4F1",
            borderRadius: "5px",
            border: "1px solid #ddd",
          }}
        >
          <Grid container spacing={1}>
            {leadEstimateNoteFields.map((field: any, idx: number) => {
              return (
                <FormField
                  // key={field.id}
                  // onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                  //   e.preventDefault()
                  // }
                  {...{
                    ...field,
                    id: field?.id,
                    variant: "outlined", // Adding this variant prop
                    isNonMarginTop: true,
                    value: estimateForm ? estimateForm[field.id] : "",
                    editReadOnly: field?.disabled || disabledField,
                    InputProps: {
                      ...field?.inputProps,
                      sx: {
                        // ...field?.inputProps?.sx,
                        padding: "2px 4px",
                        height: "36px",
                        resize: "none",
                      },
                      style: {
                        borderRadius: "25px",
                      }, // Adjust padding for all input types
                    },
                    GridFieldStyle: {
                      ...field?.GridFieldStyle,
                      marginLeft: "-16px !important",
                    },
                    onChange: onChangeAction,
                    onBlur: onBlurAction,
                    error: error && error?.[field?.id],
                  }}
                />
              );
            })}
          </Grid>
        </Grid>
        <div
          style={{
            position: "fixed",
            borderTop: "1px solid #CCC",
            left: 100,
            bottom: 48,
            right: 100,
            background: "#FFF",
            // display: "flex",
            // justifyContent: "space-between",
            padding: "10px 20px",
            zIndex: 1,
            border: "2px solid #CCC",
            boxShadow: "0 4px 6px -2px rgba(0,0,0,0.2)",
            borderBottomLeftRadius: "5px",
            borderBottomRightRadius: "5px",
          }}
        >
          <div style={{ paddingLeft: "19px" }}>
            {/* <CustomButton
              style={{ marginRight: "10px" }}
              title="Save"
              disabled={disabledField || leadDetails?.lead?.status === "JOB"}
              onClick={() => submitHandler()}
            /> */}

            <EstimateActions
              estimateForm={estimateForm}
              screenType={screenType}
              isShowActionCalled={isShowActionCalled}
              setIsShowActionCalled={setIsShowActionCalled}
              setIsFieldValueChanged={setIsFieldValueChanged}
              isFieldValueChanged={isFieldValueChanged}
              disabledField={disabledField}
            />
            {/* <CustomButton
              title="Return"
              onClick={() => {
                setShow(false);
                setEstimateForm(null);
                setError(null);
              }}
            /> */}
            {(selectedLeadEstimate || load) && screenType === "quotation" && (
              <FormControlLabel
                label="VAT Included"
                labelPlacement="start"
                control={
                  <Checkbox
                    sx={{
                      marginTop: "0",
                      marginLeft: "0 !important",
                      width: "auto !important",
                    }}
                    checked={estimateForm?.vatIncluded}
                    disabled={disabledField}
                    onChange={(e: any) => onChangeAction(e)}
                    name="vatIncluded"
                    id="vatIncluded"
                    color="primary"
                  />
                }
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "13px", // Set your desired font size here
                    display: "flex",
                  },
                }}
              />
            )}
          </div>
        </div>
      </Grid>
    </div>
  );
};

export default LeadEstimateForm;
